import React from 'react';
import { createPortal } from 'react-dom';
import { Film, Loader2, X } from 'lucide-react';
import {
  useReusableLibrary,
  pickClickThroughForReuse,
  priorApprovalBadge,
  type ReusableLibraryItem,
} from '../shared/useReusableLibrary';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import { formatDate } from '../shared/dateFormat';
import Banner from '../shared/Banner';

export interface LibraryPick {
  storagePath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  clickThroughUrl: string;
}

interface LibraryPickerModalProps {
  open: boolean;
  slug: string;
  advertiserId: string;
  onClose: () => void;
  onPick: (pick: LibraryPick) => void;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

// Compact card variant for the modal grid. Mirrors LibraryCard's thumbnail
// (muted first-frame preview via signed URL) but trims the expandable
// campaign list — the modal's job is to pick one item and close, not to
// browse history. Click anywhere on the card selects it.
//
// Thumbnail uses absolute positioning inside a relative aspect-video
// wrapper so the <video> element can't escape the box at its intrinsic
// dimensions (a portrait/square commercial would otherwise blow out the
// card and overlap its neighbor in the grid).
const PickerCard: React.FC<{
  item: ReusableLibraryItem;
  slug: string;
  onClick: () => void;
}> = ({ item, slug, onClick }) => {
  const { url, posterUrl } = useCreativePreviewUrl(slug, item.preview_storage_path);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full w-full flex-col text-left bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-slate-900 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-slate-500"
    >
      <div className="relative aspect-video w-full bg-slate-900 overflow-hidden">
        {posterUrl ? (
          // CTV-508: server-generated poster (representative non-black frame).
          <img
            src={posterUrl}
            alt={item.filename || 'Creative thumbnail'}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        ) : url ? (
          <video
            src={url}
            muted
            preload="metadata"
            playsInline
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Film className="w-6 h-6 text-white/70" />
          </div>
        )}
      </div>
      <div className="p-3 flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-900 truncate">{item.filename}</div>
        <PriorApprovalPill badge={priorApprovalBadge(item)} />
        <div className="mt-1 flex items-baseline gap-2 text-[11px] text-slate-500">
          <span className="tabular-nums">{formatBytes(item.size_bytes)}</span>
          <span className="text-slate-400">·</span>
          <span className="truncate">First used {formatDate(item.first_seen_at)}</span>
        </div>
      </div>
    </button>
  );
};

// CTV-463 / ADR-0007: per-station prior-approval state for one library entry.
// Green pill (skips review) or yellow pill (warn but allow) — null branches
// render nothing so unreviewed entries stay neutral.
const PriorApprovalPill: React.FC<{ badge: 'approved' | 'rejected' | null }> = ({ badge }) => {
  if (!badge) return null;
  if (badge === 'approved') {
    return (
      <span className="mt-1 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium">
        Approved
      </span>
    );
  }
  return (
    <span className="mt-1 inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-medium">
      Previously rejected here
    </span>
  );
};

const LibraryPickerModal: React.FC<LibraryPickerModalProps> = ({
  open,
  slug,
  advertiserId,
  onClose,
  onPick,
}) => {
  // Skip the fetch entirely when the modal is closed. The parent button is
  // already gated on hasReusable from the same hook, so the open=true path
  // is the only one that needs live data.
  const { items, isLoading, error } = useReusableLibrary(open ? slug : undefined, open ? advertiserId : undefined);

  if (!open) return null;

  const handlePick = (item: ReusableLibraryItem) => {
    onPick({
      storagePath: item.preview_storage_path,
      filename: item.filename,
      mimeType: item.mime_type ?? 'video/mp4',
      sizeBytes: item.size_bytes ?? 0,
      clickThroughUrl: pickClickThroughForReuse(item),
    });
    onClose();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-3xl max-h-[85vh] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Pick a commercial</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -mr-1 text-slate-500 hover:text-slate-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading library…
            </div>
          )}
          {!isLoading && error && (
            <Banner tone="warn" label="Couldn't load">{error}</Banner>
          )}
          {!isLoading && !error && items.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm">
              No reusable commercials yet.
            </div>
          )}
          {!isLoading && !error && items.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-stretch">
              {items.map((item) => (
                <li key={`${item.filename}::${item.size_bytes ?? ''}`} className="h-full">
                  <PickerCard item={item} slug={slug} onClick={() => handlePick(item)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default LibraryPickerModal;
